import { fade, hex, lighten, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import { drawGlow } from "./glyphs.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type {
  CaptionButton,
  ItemSlotVisual,
  RadialWedgeVisual,
  StatBarVisual,
  VisualState,
  WindowVisualState,
} from "./Theme.js";

/**
 * "Dungeon Master" — the screen behind which the campaign is run: cold wet
 * stone, iron banding, bone-colored text and everything lit by torchlight.
 * The accent is ember, and active chrome glows as if something is burning
 * just off-screen.
 */

const EMBER = hex("#d2691e");
const TORCH = hex("#f0a04b");
const BLOOD = hex("#8b1a1a");
const BONE = hex("#d8cdb8");
const IRON = hex("#4a4348");
const STONE = hex("#1c191f");
/** Draw-path colors, parsed once rather than on every call. */
const RECESS = hex("#15121a");
const RECESS_LIP = hex("#2c2532");
const IRON_RIM = hex("#4a3d2c");
const SLOT_LOCKED = hex("#171319");
const SLOT = hex("#1e1a24");
const DIALOGUE_BG = hex("#14111a");
const BRONZE_EDGE = hex("#5a4a38");
const WEDGE = hex("#241f2a");
const WEDGE_EDGE_DISABLED = hex("#332c26");
const NAMEPLATE_BG = hex("#0d0b11");
const BLACK = hex("#000000");
const HEADER_PRESSED = hex("#1b1720");

const DUNGEON_FONT = "'Georgia', 'Book Antiqua', 'Times New Roman', serif";

export class DungeonTheme extends BaseTheme {
  constructor() {
    super({
      name: "dungeon",
      isDark: true,
      metrics: makeMetrics({
        titleBarHeight: 32,
        windowBorder: 2,
        windowCornerRadius: 2,
        resizeGripSize: 6,
        captionButtonWidth: 40,
        controlCornerRadius: 2,
        controlHeight: 30,
        buttonPadding: insets(16, 6),
        buttonMinWidth: 94,
        focusRingWidth: 1,
        focusRingInset: 2,
        textBoxPadding: insets(10, 5),
        checkBoxSize: 17,
        radioSize: 17,
        scrollBarSize: 14,
        listItemHeight: 26,
        headerHeight: 28,
        font: { family: DUNGEON_FONT, size: 13.5 },
        captionFont: { family: DUNGEON_FONT, size: 14, weight: 700 },
        monoFont: { family: "Consolas, monospace", size: 12.5 },
      }),
      palette: derivePalette({
        desktop: hex("#07060a"),
        surface: STONE,
        raised: hex("#2b262f"),
        field: hex("#100e13"),
        ink: BONE,
        accent: EMBER,
        accentText: hex("#14100c"),
        border: IRON,
        titleBar: hex("#241f28"),
        titleText: TORCH,
        isDark: true,
        overrides: {
          windowBorderActive: hex("#6b5a48"),
          windowBorderInactive: hex("#332e38"),
          textSubtle: hex("#8b8177"),
          textDisabled: hex("#5d5760"),
          selectionBg: BLOOD,
          selectionText: hex("#f3e6d0"),
          selectionInactiveBg: hex("#3a2226"),
          textSelectionBg: fade(BLOOD, 0.55),
          scrollTrack: hex("#100e13"),
          scrollThumb: hex("#4a4348"),
          scrollThumbHover: hex("#5f555b"),
          scrollThumbPressed: EMBER,
          headerBg: hex("#231e27"),
          headerBgHover: hex("#2d2731"),
          headerText: TORCH,
          gridLine: hex("#332d36"),
          focusRing: TORCH,
          shadow: hex("#00000088"),
        },
      }),
    });
  }

  /** The left rivet sits where the caption would otherwise start. */
  protected override get titleTextInset(): number {
    return 22;
  }

  /** Iron banding across the caption, and torchlight when the window is active. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const b = this.metrics.windowBorder;
    const h = this.captionHeight(s);

    if (s.active) {
      // A warm wash along the top edge, as if lit from above.
      rc.fillRect({ x: b, y: b, width: bounds.width - b * 2, height: 3 }, fade(TORCH, 0.35));
    }
    rc.fillRect(
      { x: b, y: b + h - 2, width: bounds.width - b * 2, height: 2 },
      s.active ? hex("#6b5a48") : hex("#3a3440"),
    );

    // Rivets: the iron band that holds the door together. The right-hand one
    // tucks in beside the caption buttons rather than under them.
    const rivet = s.active ? hex("#8a7a63") : hex("#4a4348");
    const firstButton =
      this.captionButtonRect(bounds, "minimize", s) ??
      this.captionButtonRect(bounds, "maximize", s) ??
      this.captionButtonRect(bounds, "close", s);
    const rightRivet = (firstButton?.x ?? bounds.width - b) - 10;
    for (const x of [b + 8, rightRivet]) {
      if (x <= b + 8 + 4 && x !== b + 8) continue;
      rc.fillRect({ x, y: b + h / 2 - 2, width: 3, height: 3 }, rivet, 1.5);
    }
  }

  protected override decorateButton(rc: RenderContext, r: Rect, s: VisualState): void {
    if (s.disabled) return;
    if (s.isDefault === true || s.pressed) {
      drawGlow(rc, r, EMBER, this.controlRadius, 4);
    }
    // A bevelled iron lip along the top and bottom of the stone.
    rc.hLine(r.x + 2, r.y + 1, r.width - 4, fade(BONE, s.hovered ? 0.22 : 0.12));
    rc.hLine(r.x + 2, r.y + r.height - 2, r.width - 4, fade(hex("#000000"), 0.45));
  }

  protected override buttonFill(s: VisualState): Color {
    const p = this.palette;
    const accent = s.isDefault === true;
    if (s.disabled) return accent ? hex("#4a3a2a") : p.controlBgDisabled;
    if (s.pressed) return accent ? hex("#a04e17") : hex("#221e26");
    if (s.hovered) return accent ? TORCH : hex("#3a3440");
    return accent ? EMBER : p.controlBg;
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? hex("#5f1212") : BLOOD;
    return pressed ? hex("#3a3440") : hex("#332d36");
  }

  protected override closeGlyphInk(): Color {
    return hex("#f3e6d0");
  }

  // --- game chrome --------------------------------------------------------

  override drawStatBarFrame(rc: RenderContext, r: Rect, s: StatBarVisual): void {
    // A bar chiselled into stone: a dark recess with a lip of lit rock along
    // the top, and torchlight round it when the reading is dangerous.
    rc.fillRect(r, RECESS, 2);
    rc.hLine(r.x + 1, r.y, r.width - 2, RECESS_LIP);
    rc.strokeRect(r, s.low && !s.disabled ? TORCH : IRON_RIM, 1, 2);
  }

  override drawItemSlot(rc: RenderContext, r: Rect, s: ItemSlotVisual): void {
    // An iron-bound recess. Locked slots are barred over rather than grayed:
    // in a dungeon a slot you cannot use is a slot that is shut.
    rc.fillRect(r, s.locked ? SLOT_LOCKED : SLOT, 2);
    rc.strokeRect(r, s.highlighted ? TORCH : s.rarity ?? IRON_RIM, 1, 2);
    if (s.hovered && !s.locked) rc.strokeRect(r, fade(TORCH, 0.5), 1, 2);
    if (!s.locked) return;
    const inset = 4;
    rc.drawLine(
      { x: r.x + inset, y: r.y + inset },
      { x: r.x + r.width - inset, y: r.y + r.height - inset },
      1,
      IRON_RIM,
    );
  }

  override drawDialogueBox(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, fade(DIALOGUE_BG, 0.95), 2);
    rc.strokeRect(r, BRONZE_EDGE, 1, 2);
    // A thin ember line inside the frame, the way every lit surface in this
    // theme carries one.
    rc.strokeRect(
      { x: r.x + 3, y: r.y + 3, width: r.width - 6, height: r.height - 6 },
      fade(EMBER, 0.55),
      1,
      1,
    );
  }

  override drawRadialWedge(rc: RenderContext, w: RadialWedgeVisual): void {
    const fill = w.disabled
      ? SLOT_LOCKED
      : w.highlighted
        ? TORCH
        : fade(w.color ?? WEDGE, 0.95);
    rc.fillArc(w.center, w.innerRadius, w.outerRadius, w.startAngle, w.endAngle, fill);
    // A cut edge round the outside, as if the wheel were chiselled.
    rc.fillArc(
      w.center,
      w.outerRadius - 1.5,
      w.outerRadius,
      w.startAngle,
      w.endAngle,
      w.disabled ? WEDGE_EDGE_DISABLED : BRONZE_EDGE,
    );
  }

  override drawNameplateFrame(rc: RenderContext, r: Rect): void {
    // A scrim behind the name: torchlight is dark and a plain caption is lost
    // against it, which is the whole reason this hook exists.
    rc.fillRect(r, fade(NAMEPLATE_BG, 0.6), 2);
  }

  override drawFloatingText(
    rc: RenderContext,
    text: string,
    x: number,
    y: number,
    font: FontSpec,
    color: Color,
  ): void {
    // Outlined, because a damage number over a lit floor is otherwise a smear.
    const shadow = fade(BLACK, color.a * 0.85);
    for (const [dx, dy] of [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ] as const) {
      rc.drawText(text, x + dx, y + dy, font, shadow, { baseline: "middle" });
    }
    rc.drawText(text, x, y, font, color, { baseline: "middle" });
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? HEADER_PRESSED : s.hovered ? p.headerBgHover : p.headerBg);
    rc.hLine(r.x, r.y + r.height - 1, r.width, BRONZE_EDGE);
    rc.vLine(r.x + r.width - 1, r.y + 5, r.height - 10, p.gridLine);
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const i = this.metrics.focusRingInset;
    rc.strokeRect(
      { x: r.x + i, y: r.y + i, width: r.width - i * 2, height: r.height - i * 2 },
      lighten(TORCH, 0.1),
      1,
      Math.max(0, this.controlRadius - 1),
    );
  }
}
